import React, { useRef } from "react"
import { useDrag, useDrop } from "react-dnd"
import {
  Paper,
  Stack,
  Typography,
  Avatar,
  IconButton,
  Chip,
  Box,
  TextField,
  Divider,
  Collapse,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import BuildIcon from "@mui/icons-material/Build"
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing"
import AddIcon from "@mui/icons-material/Add"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import { useTranslation } from "react-i18next"
import { ItemTypes, useVisualEditor } from "../VisualEditorContext"
import StepFileUpload from "../../StepFileUpload"

const StepCard = ({ step, index }) => {
  const { t } = useTranslation()
  const ref = useRef(null)
  const { moveStep, deleteStep, updateStep, removeMaterialFromStep, updateMaterialQuantity, addMaterialToStep } = useVisualEditor()
  const [expanded, setExpanded] = React.useState(true)

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemTypes.STEP,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  // Accept other steps for reordering
  const [, dropStep] = useDrop({
    accept: ItemTypes.STEP,
    hover(item) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      moveStep(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  // Accept materials for adding to this step
  const [{ isOverMaterial, canDropMaterial }, dropMaterial] = useDrop({
    accept: ItemTypes.MATERIAL,
    drop: (item) => {
      addMaterialToStep(index, item.material)
    },
    collect: (monitor) => ({
      isOverMaterial: monitor.isOver(),
      canDropMaterial: monitor.canDrop(),
    }),
  })

  // Combine refs
  preview(dropStep(ref))

  const isDropActive = isOverMaterial && canDropMaterial

  // Calculate step cost
  const stepCost = (step.materials || []).reduce((sum, mat) => {
    const qty = parseFloat(mat.quantity) || 0
    const cost = parseFloat(mat.costprice) || 0
    return sum + (qty * cost)
  }, 0)

  return (
    <Paper
      ref={ref}
      elevation={0}
      sx={{
        p: 2,
        opacity: isDragging ? 0.5 : 1,
        border: isDropActive ? "2px dashed #3b82f6" : "1px solid #e2e8f0",
        borderRadius: 2,
        bgcolor: isDropActive ? "#eff6ff" : "#ffffff",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: "#cbd5e1",
        },
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <IconButton
          ref={drag}
          size="small"
          sx={{ cursor: "grab", color: "#94a3b8", "&:active": { cursor: "grabbing" } }}
        >
          <DragIndicatorIcon />
        </IconButton>

        <Avatar
          sx={{
            bgcolor: "#3b82f6",
            color: "#ffffff",
            width: 32,
            height: 32,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {step.seq}
        </Avatar>

        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
            {step.typid}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
            <Chip
              size="small"
              icon={<BuildIcon sx={{ fontSize: 12 }} />}
              label={`${step.materials?.length || 0} ${t("materials")}`}
              sx={{
                height: 20,
                fontSize: "0.7rem",
                bgcolor: step.materials?.length > 0 ? "#dbeafe" : "#f1f5f9",
                color: step.materials?.length > 0 ? "#3b82f6" : "#64748b",
                "& .MuiChip-icon": {
                  color: step.materials?.length > 0 ? "#3b82f6" : "#64748b",
                },
              }}
            />
            {stepCost > 0 && (
              <Chip
                size="small"
                label={`€${stepCost.toFixed(2)}`}
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  bgcolor: "#dcfce7",
                  color: "#16a34a",
                  fontWeight: 600,
                }}
              />
            )}
            {step.files?.length > 0 && (
              <Chip
                size="small"
                icon={<AttachFileIcon sx={{ fontSize: 12 }} />}
                label={`${step.files.length}`}
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  bgcolor: "#f3e8ff",
                  color: "#8b5cf6",
                  "& .MuiChip-icon": { color: "#8b5cf6" },
                }}
              />
            )}
          </Stack>
        </Box>

        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ color: "#64748b" }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>

          <IconButton
            size="small"
            color="error"
            onClick={() => deleteStep(index)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {/* Drop zone hint when dragging material */}
      {isDropActive && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: "#dbeafe",
            textAlign: "center",
          }}
        >
          <AddIcon sx={{ color: "#3b82f6", mb: 0.5 }} />
          <Typography variant="body2" color="#3b82f6" fontWeight={500}>
            {t("Drop material here")}
          </Typography>
        </Box>
      )}

      {/* Expanded Content */}
      <Collapse in={expanded}>
        <Box ref={dropMaterial} sx={{ mt: 2, minHeight: 50 }}>
          {/* Materials List */}
          {step.materials && step.materials.length > 0 ? (
            <>
              <Typography variant="caption" color="#64748b" fontWeight={600} sx={{ mb: 1, display: "block" }}>
                {t("Materials")}
              </Typography>
              <Stack spacing={1} sx={{ mb: 2 }}>
                {step.materials.map((mat, matIndex) => {
                  const isMaterial = mat.usageType === "MATERIAL"
                  const matCost = (parseFloat(mat.quantity) || 0) * (parseFloat(mat.costprice) || 0)

                  return (
                    <Paper
                      key={matIndex}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        borderColor: "#e2e8f0",
                      }}
                    >
                      {isMaterial ? (
                        <BuildIcon sx={{ fontSize: 18, color: "#3b82f6" }} />
                      ) : (
                        <PrecisionManufacturingIcon sx={{ fontSize: 18, color: "#d97706" }} />
                      )}
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500, color: "#1e293b" }}>
                        {mat.name}
                      </Typography>
                      <Chip
                        label={mat.unit}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: "#e2e8f0", color: "#64748b" }}
                      />
                      <TextField
                        size="small"
                        type="text"
                        value={mat.quantity || ""}
                        onChange={(e) => updateMaterialQuantity(index, matIndex, e.target.value)}
                        placeholder="0"
                        sx={{
                          width: 70,
                          "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
                        }}
                        inputProps={{ style: { textAlign: "center", fontSize: "0.875rem" } }}
                      />
                      {matCost > 0 && (
                        <Typography variant="caption" fontWeight={600} color="#16a34a" sx={{ minWidth: 60, textAlign: "right" }}>
                          €{matCost.toFixed(2)}
                        </Typography>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeMaterialFromStep(index, matIndex)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  )
                })}
              </Stack>
            </>
          ) : (
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                border: "2px dashed #e2e8f0",
                textAlign: "center",
                color: "#94a3b8",
              }}
            >
              <AddIcon sx={{ fontSize: 24, opacity: 0.5, mb: 0.5 }} />
              <Typography variant="body2">
                {t("Drag materials here from the right panel")}
              </Typography>
            </Box>
          )}

          {/* Instructions */}
          <Divider sx={{ my: 1.5, borderColor: "#f1f5f9" }} />
          <Typography variant="caption" color="#64748b" fontWeight={600} sx={{ mb: 1, display: "block" }}>
            {t("Instructions")}
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder={t("Add production instructions for this step...")}
            value={step.instructions || ""}
            onChange={(e) => updateStep(index, { instructions: e.target.value })}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />

          {/* Files */}
          <Divider sx={{ my: 1.5, borderColor: "#f1f5f9" }} />
          <Typography variant="caption" color="#64748b" fontWeight={600} sx={{ mb: 1, display: "block" }}>
            {t("Attachments")}
          </Typography>
          <StepFileUpload
            files={step.files || []}
            onFilesChange={(newFiles) => updateStep(index, { files: newFiles })}
            stepName={step.typid}
          />
        </Box>
      </Collapse>
    </Paper>
  )
}

export default StepCard
